<template>
	<div
		class="daily-quest-box"
		v-if="quest"
		:class="[
			{
				'daily-quest-box--completed':
					questCompleted && !questActionRequired,
			},
			{ 'daily-quest-box--finished': finished },
		]"
	>
		<div v-if="quest" class="daily-quest-box__content">
			<DailyQuestBoxRewards
				:quest="quest"
				:questCompleted="questCompleted && !questActionRequired"
			>
				<ShrinkText
					v-if="finished"
					:text="translate('ui.claimed')"
					:fontSize="18"
					:isBold="true"
					class="daily-quest-box--claimed"
				/>
				<AppTextButton
					v-else
					class="claim-rewards-btn"
					:type="StatusTypes.SUCCESS"
					:text="translate('button.claim')"
					:pulse="true"
					:disabled="!claimRewardButtonShow"
					:notification="claimRewardButtonShow ? '!' : undefined"
					@click="handleClaimRewards"
				/>
			</DailyQuestBoxRewards>
			<QuestTask
				v-if="quest"
				:current="quest.progress"
				:target="getAmount(quest.objective.amount)"
				:building="hasType(quest.objective) ? quest.objective.type : ''"
				:text="translate(quest.assignment)"
				:hint="quest.hintText ? translate(quest.hintText) : ''"
				:building-level="
					hasLevel(quest.objective) ? quest.objective.level : 0
				"
				:quest-action-required="questActionRequired"
				:town="getTownName"
				:icon="hasType(quest.objective) ? quest.objective.type : ''"
				isDaily
			/>
			<slot />
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import DailyQuestBoxRewards from "@/components/Quests/DailyQuestBoxRewards.vue";
import QuestTask from "@/components/Quests/QuestTask.vue";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { settlementNames } from "@/game/entities/config/settlements";
import { hasLevel, hasSettlementId, hasType } from "@/game/QuestRepository";
import type { QuestActive } from "@/types/quest";
import { StatusTypes } from "@/types/ui";
import { getAmount } from "@/utils/number";
import { translate } from "@/utils/translate";
import { computed } from "vue";
import ShrinkText from "../ShrinkText.vue";

interface Props {
	quest: QuestActive | null;
	finished?: boolean;
}

const emit = defineEmits(["action", "claimRewards"]);

const { quest = null, finished = false } = defineProps<Props>();

const questCompleted = computed((): boolean => {
	if (!quest) {
		return false;
	}
	return quest.progress >= getAmount(quest.objective.amount);
});

const claimRewardButtonShow = computed((): boolean => {
	if (!quest || finished) {
		return false;
	}

	if (quest?.payed === 0) {
		return false;
	} else if (quest?.payed === 1) {
		return true;
	}

	return questCompleted.value;
});

const handleClaimRewards = (): void => {
	emit("claimRewards");
};

const questActionRequired = computed((): boolean => {
	return (
		quest?.objective.action === QuestObjectiveActions.USE_RESOURCES &&
		quest.progress >= getAmount(quest.objective.amount) &&
		quest?.payed === 0
	);
});

const getTownName = computed((): string => {
	if (!quest || !quest.objective || !hasSettlementId(quest.objective)) {
		return "";
	}

	const actualSettlement = settlementNames.get(quest.objective.settlement_id);
	if (!actualSettlement) {
		return "";
	}

	return actualSettlement.name;
});
</script>

<style lang="scss" scoped>
.daily-quest-box {
	position: relative;
	gap: 0.5rem;
	margin: 0.5rem 0 0;
	width: 100%;
	max-width: 25rem;
	height: auto;
	&--finished {
		opacity: 0.5;
	}

	span {
		color: #372615 !important;
		font-size: 0.813rem;
		text-shadow: none !important;
	}

	&__content {
		@include flex-col-center;
		gap: 1rem;
		margin-bottom: 1.5rem;
		padding: 0 0.5rem 1.25rem;

		.quest-description {
			text-align: center;
		}
	}

	&__header {
		position: relative;
	}

	:deep(.box-header-bg) {
		position: relative;
		width: 100%;
		height: 4.625rem;

		.solid-bg-polygon {
			fill: rgba(145, 113, 56, 0.8) !important;
		}

		.texture-bg-polygon {
			opacity: 0.2 !important;
		}
	}

	&-reward {
		@include flex-row-center;
		margin-top: 0.75rem;

		&-value {
			margin-right: 0.25rem;
			margin-bottom: 0.25rem;
			font-size: 1rem;
		}

		&:not(:last-child) {
			margin-right: 0.75rem;
		}
	}

	&-description {
		text-align: center;
	}

	.claim-rewards-btn {
		position: relative;
		z-index: 1;
		width: 8rem;
		height: 2rem;
		:deep(.text-button) {
			height: 2rem;
		}
	}
	&--claimed {
		justify-content: flex-end;
		height: 2.5rem;
		color: #b6dd00;
		font-weight: 800;
		font-size: 17px;
		text-shadow: 0.7px 0.7px 1px rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}
}
</style>
